import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Users, Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export function UniverseAccountsDialog({ row, onOpenChange }) {
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["uni-accounts", row?.id, search],
    queryFn: async () => (await api.get(`/master-data/${row.id}/accounts`, { params: { search } })).data,
    enabled: !!row?.id,
    keepPreviousData: true,
  });
  const items = data?.items || [];
  const close = (v) => { if (!v) { setSearch(""); onOpenChange(null); } };
  return (
    <Dialog open={!!row} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="universe-accounts-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-400" /> {row?.company} — Accounts
            {data && <span className="text-sm font-normal text-slate-400">{data.total} contact{data.total === 1 ? "" : "s"}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, designation, email, city…" className="h-9 pl-9" data-testid="uni-accounts-search" />
        </div>
        {isLoading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="uni-accounts-empty">No contacts {search ? "match your search" : "recorded for this company yet"}.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Name", "Designation", "Email", "Phone", "Location"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`uni-account-row-${c.id}`}>
                    <TableCell className="font-medium text-slate-800 whitespace-nowrap">{c.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.designation || "—"}</TableCell>
                    <TableCell className="text-sm">{c.email ? <a href={`mailto:${c.email}`} className="text-sky-700 hover:underline">{c.email}</a> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{c.phone || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500 whitespace-nowrap">{c.location || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && data.total > items.length && (
          <p className="text-[11px] text-slate-400">Showing first {items.length} of {data.total} — use search to narrow down.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
